<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { useI18n } from 'vue-i18n'
import { ArrowDown } from '@element-plus/icons-vue'
import { useCompteurStore } from '@/store/compteur.store'
import { useMountingPlanStore } from '@/store/mountingPlan.store'
import { getDateFrenchFormat } from '@/helper/dateFormatter'

// Lot9 / E30 — Sélecteur de plan de monte (P.D.M) sur l'écran Compteurs, équivalent du popup
// "P.D.M" (liste P01/P02/P03) d'OGICOM legacy (cf. Documents/compteurs_ogicom.png). Contrairement
// au legacy, on ne "filtre" pas l'affichage par plan : choisir un plan ici déclenche un RAZ complet
// pour le jour affiché (seul mécanisme backend existant pour rattacher un plan au « Prévu »,
// cf. CompteurRebuildService.RazAsync / REQ892). Le badge affiche le plan du DERNIER RAZ du jour
// (compteurStore.latestRaz), donc "on sait lequel a été sélectionné" avant de rechanger.

const props = defineProps<{
  /** Jour affiché sur l'écran (ISO locale, YYYY-MM-DD). */
  currentDate: string
}>()

const { t } = useI18n()
const compteurStore = useCompteurStore()
const mountingPlanStore = useMountingPlanStore()

const isSwitching = ref(false)

const currentPlanId = computed(() => compteurStore.latestRaz?.mountingPlanId ?? null)

const currentPlanLabel = computed(() => {
  if (currentPlanId.value == null) return t('compteur.planDeMonte.noPlan')
  const plan = mountingPlanStore.datas.find((p) => p.id === currentPlanId.value)
  return plan ? plan.name : `#${currentPlanId.value}`
})

// REQ892 : liste réelle des plans, triée du plus récent au plus ancien.
const mountingPlanOptions = computed(() => [...mountingPlanStore.datas]
  .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
  .map((plan) => ({ id: plan.id, label: `${getDateFrenchFormat(plan.date)} — ${plan.name}` })))

watch(() => props.currentDate, () => {
  if (mountingPlanStore.datas.length === 0) mountingPlanStore.load({ page: 1, pageSize: 0 })
}, { immediate: true })

async function selectPlan(planId: number | null): Promise<void> {
  if (planId === currentPlanId.value || isSwitching.value) return

  const plan = planId != null ? mountingPlanStore.datas.find((p) => p.id === planId) : null
  const confirmMessage = planId != null
    ? t('compteur.planDeMonte.confirmMessageWithPlan', { plan: plan?.name ?? planId })
    : t('compteur.planDeMonte.confirmMessageWithoutPlan')
  try {
    await ElMessageBox.confirm(
      confirmMessage,
      planId != null ? t('compteur.planDeMonte.confirmTitleChange') : t('compteur.planDeMonte.confirmTitleRemove'),
      {
        confirmButtonText: t('common.confirm'),
        cancelButtonText: t('common.cancel'),
        confirmButtonClass: 'el-confirm-messagebox-btn',
        cancelButtonClass: 'el-cancel-messagebox-btn',
        type: 'warning',
      },
    )
  } catch {
    return
  }

  isSwitching.value = true
  const result = await compteurStore.raz({ date: props.currentDate, mountingPlanId: planId, dateReference: null })
  isSwitching.value = false

  if (result.error) {
    ElMessage({ type: 'error', message: t('compteur.planDeMonte.errorMessage'), duration: 4000 })
    return
  }
  ElMessage({
    type: 'success',
    message: planId != null
      ? t('compteur.planDeMonte.successMessageWithPlan', { plan: plan?.name ?? planId })
      : t('compteur.planDeMonte.successMessageWithoutPlan'),
    duration: 3000,
  })
}
</script>

<template>
  <el-popover placement="bottom-start" :width="280" trigger="click" popper-class="plan-de-monte-popover">
    <template #reference>
      <button type="button" class="plan-de-monte-trigger">
        <span class="plan-de-monte-trigger__label">{{ $t('compteur.planDeMonte.trigger', { plan: currentPlanLabel }) }}</span>
        <el-icon class="plan-de-monte-trigger__chevron"><ArrowDown /></el-icon>
      </button>
    </template>

    <div class="plan-de-monte-panel" v-loading="isSwitching">
      <div class="plan-de-monte-panel__title">{{ $t('compteur.planDeMonte.panelTitle') }}</div>
      <ul class="plan-de-monte-list">
        <li
          v-for="opt in mountingPlanOptions"
          :key="opt.id"
          class="plan-de-monte-list__item"
          :class="{ 'plan-de-monte-list__item--active': opt.id === currentPlanId }"
          @click="selectPlan(opt.id)"
        >
          {{ opt.label }}
        </li>
        <li v-if="mountingPlanOptions.length === 0" class="plan-de-monte-list__empty">{{ $t('compteur.planDeMonte.emptyText') }}</li>
        <li
          class="plan-de-monte-list__item plan-de-monte-list__item--remove"
          :class="{ 'plan-de-monte-list__item--active': currentPlanId == null }"
          @click="selectPlan(null)"
        >
          {{ $t('compteur.planDeMonte.removeOption') }}
        </li>
      </ul>
    </div>
  </el-popover>
</template>

<style scoped>
.plan-de-monte-trigger {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px;
  background: #ffffff;
  color: #2b48a2;
  border: 1px solid #2b48a2;
  border-radius: 16px;
  cursor: pointer;
  font-family: inherit;
}

.plan-de-monte-trigger__label {
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
}

.plan-de-monte-trigger__chevron {
  font-size: 12px;
}

.plan-de-monte-panel__title {
  font-weight: 700;
  font-size: 15px;
  color: #1c1e21;
  margin-bottom: 8px;
}

.plan-de-monte-list {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 260px;
  overflow-y: auto;
  border: 1px solid #2b48a2;
}

.plan-de-monte-list__item {
  padding: 6px 10px;
  font-size: 13px;
  cursor: pointer;
  border-bottom: 1px solid #e5e7eb;
}

.plan-de-monte-list__item:last-child {
  border-bottom: none;
}

.plan-de-monte-list__item:hover {
  background-color: #eef1fb;
}

.plan-de-monte-list__item--active {
  background-color: #2b48a2;
  color: #ffffff;
  font-weight: 700;
}

/* Le survol d'un item déjà sélectionné ne doit pas repasser au bleu clair (moins spécifique
   que .item:hover) : on garde le fond foncé, juste légèrement éclairci, texte blanc conservé. */
.plan-de-monte-list__item--active:hover {
  background-color: #35529c;
  color: #ffffff;
}

.plan-de-monte-list__empty {
  padding: 10px;
  font-size: 13px;
  color: #6b7280;
  text-align: center;
}

.plan-de-monte-list__item--remove {
  color: #c0392b;
  font-style: italic;
  border-top: 1px solid #e5e7eb;
}

.plan-de-monte-list__item--remove:hover {
  background-color: #fdecea;
}

.plan-de-monte-list__item--remove.plan-de-monte-list__item--active {
  background-color: #c0392b;
  color: #ffffff;
}
</style>
